import { createRoute, useNavigate, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { customVaultDefinitionSchema } from '@siliconvault/shared';
import type { CustomFieldDef } from '@siliconvault/shared';
import { Input, Textarea } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { VaultFieldEditor } from '../components/vault/VaultFieldEditor';
import { useVault } from '../context/VaultContext';
import { useTranslation } from '../i18n';
import { Shell } from '../components/layout/Shell';
import { Route as rootRoute } from './__root';
import styles from './vaults.custom.module.css';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/vaults/settings',
  component: VaultSettingsPage,
});

function VaultSettingsPage() {
  const { activeVault, updateVaultDefinition } = useVault();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const def = activeVault?.customDefinition;

  const [displayName, setDisplayName] = useState(def?.displayName ?? '');
  const [description, setDescription] = useState(def?.description ?? '');
  const [platformLabel, setPlatformLabel] = useState(def?.platformLabel ?? 'Brand');
  const [fields, setFields] = useState<CustomFieldDef[]>(def?.fields ?? []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  if (!activeVault || !def) {
    return (
      <Shell>
        <div className={styles.page}>
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 40 }}>
            {t('vaults.settings.notCustom')}
          </p>
        </div>
      </Shell>
    );
  }

  const handleSave = async () => {
    setError('');
    setSaved(false);

    const updated = {
      displayName,
      description,
      icon: def.icon,
      platformLabel: platformLabel || 'Brand',
      fields,
    };

    const validation = customVaultDefinitionSchema.safeParse(updated);
    if (!validation.success) {
      setError(validation.error.errors.map((e) => e.message).join('. '));
      return;
    }

    setSaving(true);
    try {
      await updateVaultDefinition(activeVault.id, validation.data);
      setSaved(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('vaults.settings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Shell>
      <div className={styles.page}>
        <div className={styles.header}>
          <Link to="/dashboard" className={styles.backLink}>
            <ChevronLeft size={16} />
            {t('vaults.settings.backToDashboard')}
          </Link>
          <h1 className={styles.title}>{t('vaults.settings.title')}</h1>
          <p className={styles.subtitle}>{t('vaults.settings.subtitle')}</p>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>{t('vaults.settings.vaultInfo')}</h2>
          <Input
            label={t('vaults.custom.vaultName')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
          <div style={{ height: 12 }} />
          <Textarea
            label={t('vaults.custom.description')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
          />
          <div style={{ height: 12 }} />
          <Input
            label={t('vaults.custom.platformLabel')}
            value={platformLabel}
            onChange={(e) => setPlatformLabel(e.target.value)}
          />
        </div>

        <VaultFieldEditor
          fields={fields}
          onFieldsChange={setFields}
          platformLabel={platformLabel}
        />

        {error && <p className={styles.errorMsg}>{error}</p>}
        {saved && (
          <p style={{ fontSize: '0.8125rem', color: 'var(--success, #22c55e)', margin: '8px 0 0' }}>
            {t('vaults.settings.saved')}
          </p>
        )}

        <div className={styles.footer}>
          <Button variant="secondary" onClick={() => navigate({ to: '/dashboard' })}>
            {t('vaults.cancel')}
          </Button>
          <Button
            variant="primary"
            loading={saving}
            disabled={saving || !displayName.trim() || fields.length === 0}
            onClick={handleSave}
          >
            {t('vaults.settings.saveChanges')}
          </Button>
        </div>
      </div>
    </Shell>
  );
}
